import React, { useMemo, useRef, useState } from 'react';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { SingleArrowDown, SingleArrowUp } from '@r1-frontend/ui-react/components/svg/arrows';
import { Paragraph4, Paragraph5 } from '@r1-frontend/ui-react/components/typography/paragraph';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useResizeObserver } from '@r1-frontend/shared/hooks/useResizeObserver';

import { IReviewData } from '~/src/features/reviews';

import * as ST from './styled';

const avatar = '/static/images/grades/avatar_';
const starFull = '/static/images/grades/star-full';
const starEmpty = '/static/images/grades/star-empty';

export interface IReview {
    data: IReviewData,
    order: number,
}

const Review = ({ data, order }: IReview) => {
    const { date, text, grade, autor, source } = data;
    const avatarIndex = order % 16;
    const [isShowAllLines, setIsShowAllLines] = useState(false);
    const isMobile = useMatchMedia(wideBreakpoints.mobile);
    const linesNumber = isMobile ? 4 : 3;
    const lineHeight = isMobile ? 20 : 24;
    const ref = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver(ref);
    const [overflow, setOverflow] = useState('hidden');

    const isOverflowing = useMemo((): boolean => {
        const el: HTMLDivElement | null = ref.current;
        if (el) {
            const curOverf = el.style.overflow;
            if (!curOverf || curOverf === 'visible') {
                setOverflow('hidden');
            }
            const isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
            setOverflow(curOverf);
            const divHeight = el.offsetHeight;
            const lines = divHeight / lineHeight;
            return isOverflowing || lines > linesNumber;
        }
        return true;
    }, [width, linesNumber, lineHeight]);


    return (
        <ST.Wrapper>
            <ST.User>
                <ST.UserInfo>
                    <WebpImage urlWebp={`${avatar + avatarIndex}.webp`}>
                        <ST.Avatar data-test='review-avatar' src={`${avatar + avatarIndex}.png`} />
                    </WebpImage>
                    <ST.UserDetail>
                        <Paragraph4 data-test='review-autor'>
                            {autor}
                        </Paragraph4>
                        <Paragraph5
                            data-test='review-date'
                            $color={COLORS.TextInfo}
                        >
                            {`${date} (${source})`}
                        </Paragraph5>
                    </ST.UserDetail>
                </ST.UserInfo>
                <ST.Grades data-test='review-grades'>
                    {
                        [...Array(5)].map((_, index) => {
                            return (<WebpImage
                                key={`${date}_grade_star_${index}`}
                                urlWebp={`${index < grade ? starFull : starEmpty}.webp`}
                            >
                                <ST.Star data-test='grade-help-image' src={`${index < grade ? starFull : starEmpty}.png`} />
                            </WebpImage>);
                        })
                    }
                </ST.Grades>
            </ST.User>
            <ST.Text
                ref={ref}
                data-test='review-text'
                $color={COLORS.TextSecondary}
                isShowAllLines={isShowAllLines}
                linesNumber={linesNumber}
                overflow={overflow}
            >
                {text}
            </ST.Text>
            {isOverflowing &&
                <ST.ShowLinesBtn
                    data-test='review-btn'
                    size={'medium'}
                    bType={'ghost'}
                    onClick={() => setIsShowAllLines(prev => !prev)}
                >
                    {isShowAllLines ? <SingleArrowUp size={24} /> : <SingleArrowDown size={24} />}
                    {isShowAllLines ? 'Свернуть' : 'Читать полностью'}
                </ST.ShowLinesBtn>
            }
        </ST.Wrapper>
    );
};

export default Review;
